import {
  Box,
  HStack,
  Portal,
  Select,
  Text,
  VStack,
  createListCollection
} from "@chakra-ui/react"
import type { IconName } from "@pathwright/pathicons"
import { useMemo, type ComponentProps, type ReactNode } from "react"
import Pathicon from "../pathicon/Pathicon"
import {
  SELECTOR_SIZE,
  SELECTOR_TRIGGER_PROPS,
  SELECTOR_VARIANT
} from "./selectorStyles"

export type CustomSelectOption = {
  value: string
  label: string
  icon?: IconName
  /** Shown under the label in the menu only, not in the trigger. */
  description?: string
  disabled?: boolean
}

export type CustomSelectProps = {
  value?: string
  placeholder?: string
  disabled?: boolean
  options: CustomSelectOption[]
  onChange: (value: string) => void
  size?: "sm" | "md"
  minW?: string
  w?: string
  triggerProps?: ComponentProps<typeof Select.Trigger>
  contentProps?: ComponentProps<typeof Select.Content>
}

function renderOptionLabel({ icon, label }: CustomSelectOption): ReactNode {
  if (!icon) return label

  return (
    <HStack gap={1} display="inline-flex" alignItems="center">
      <Pathicon icon={icon} />
      <Text as="span" m={0} fontSize="inherit">
        {label}
      </Text>
    </HStack>
  )
}

function renderOptionContent(option: CustomSelectOption): ReactNode {
  if (!option.description) return renderOptionLabel(option)

  return (
    <VStack align="stretch" gap={0.5} py={1}>
      {renderOptionLabel(option)}
      <Text
        as="span"
        m={0}
        fontSize="xs"
        lineHeight="short"
        color="fg.muted"
        whiteSpace="normal"
        // Line the description up with the label, past the icon.
        ps={option.icon ? 5 : 0}
      >
        {option.description}
      </Text>
    </VStack>
  )
}

function renderSelectedOptionContent({
  icon,
  label
}: CustomSelectOption): ReactNode {
  if (!icon) {
    return (
      <Text as="span" m={0} fontSize="inherit" truncate>
        {label}
      </Text>
    )
  }

  return (
    <HStack gap={1} alignItems="center" minW={0} maxW="full" overflow="hidden">
      <Box flexShrink={0} display="inline-flex">
        <Pathicon icon={icon} />
      </Box>
      <Text as="span" m={0} fontSize="inherit" truncate minW={0}>
        {label}
      </Text>
    </HStack>
  )
}

export default function CustomSelect({
  value = "",
  placeholder = "Select...",
  disabled,
  options,
  onChange,
  size = SELECTOR_SIZE,
  minW = "180px",
  w = "auto",
  triggerProps,
  contentProps
}: CustomSelectProps) {
  const collection = useMemo(
    () =>
      createListCollection({
        items: options.map((option) => ({
          value: option.value,
          label: option.label
        }))
      }),
    [options]
  )

  const selectedOption = options.find((option) => option.value === value)

  return (
    <Select.Root
      collection={collection}
      value={value ? [value] : []}
      onValueChange={(details) => {
        const nextValue = details.value[0]
        if (nextValue) onChange(nextValue)
      }}
      disabled={disabled}
      size={size}
      variant={SELECTOR_VARIANT}
      width={w}
      minW={minW}
      positioning={{ sameWidth: true }}
    >
      <Select.Control minW={0}>
        <Select.Trigger
          {...SELECTOR_TRIGGER_PROPS}
          overflow="hidden"
          pe="8"
          gap={0}
          {...triggerProps}
        >
          <Select.ValueText
            placeholder={placeholder}
            flex="1"
            minW={0}
            overflow="hidden"
            maxW="full"
          >
            {selectedOption
              ? renderSelectedOptionContent(selectedOption)
              : null}
          </Select.ValueText>
        </Select.Trigger>
        <Select.IndicatorGroup>
          <Select.Indicator>
            <Pathicon icon="chevron-down" />
          </Select.Indicator>
        </Select.IndicatorGroup>
      </Select.Control>
      <Portal>
        <Select.Positioner>
          <Select.Content borderRadius="15px" {...contentProps}>
            {options.map((option) => (
              <Select.Item
                key={option.value}
                item={option.value}
                _disabled={{ opacity: 0.5, cursor: "not-allowed" }}
              >
                {renderOptionContent(option)}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Positioner>
      </Portal>
    </Select.Root>
  )
}
